/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useState } from 'react';
import AI from '../../../../domain/models/AI';
import { ContextStatus, AIStatus } from '@leedpulse-monorepo/enums';
import updateAI from '../../../../domain/use_cases/ai/updateAI';
import AIGender from '../../../../domain/enums/ai/AIGender';
import AITon from '../../../../domain/enums/ai/AITon';
import { IAI } from '@leedpulse-monorepo/interfaces';

type AIReceptionist = NonNullable<IAI['ai_receptionist']>;

const AIEditionFormContext = createContext({
  wipAI: null as AI | null,
  status: ContextStatus.IDLE,
  initWithAI: (_: AI) => { },
  submitUpdate: () => { },
  onActionChange: (_: string) => { },
  onBehaviourChange: (_: string) => { },
  onConstraintsChange: (_: string) => { },
  onDomainChange: (_: string) => { }, // NOTE : This has become a domainId
  onFullnameChange: (_: string) => { },
  onGenderChange: (_: AIGender) => { },
  onIntroChange: (_: string) => { },
  onLocationChange: (_: string) => { },
  onNameChange: (_: string) => { },
  onStatusChange: (_: AIStatus) => { },
  onProfileChange: (_: string) => { },
  onFirstReplyTimingChange: (_: number) => { },
  onSecondReplyTimingChange: (_: number) => { },
  onFirstReminderTimingChange: (_: number) => { },
  onSecondReminderTimingChange: (_: number) => { },
  onFlowTimingChange: (_: number) => { },
  onTargetChange: (_: string) => { },
  onTonChange: (_: AITon) => { },
  onWebsiteChange: (_: string) => { },
  onFirstMessageChange: (_: string) => { },
  onQualificationCriteriaChange: (_: string) => { },
  onVoiceIdChange: (_?: string) => { },
  onLanguageChange: (_?: string) => { },
  onPersonalityChange: (_: string) => { },
  onEnvironmentChange: (_: string) => { },
  onToneChange: (_: string) => { },
  onGoalChange: (_: string) => { },
  onClosingChange: (_: string) => { },
  onGuardrailsChange: (_: string) => { },
  onOtherChange: (_: string) => { },
  onActivitiesChange: (_: AIReceptionist['activities']) => { },
  onMotifsChange: (_: AIReceptionist['motifs']) => { },
});

export const AIEditionFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [wipAI, setWipAI] = useState(null as AI | null);
  const [savedAI, setSavedAI] = useState(null as AI | null);

  const [status, setStatus] = useState(ContextStatus.IDLE);

  const initWithAI = async (subscription: AI) => {
    setWipAI(subscription);
    setSavedAI(subscription);
  };

  const submitUpdate = async () => {
    if (wipAI === null) return;
    setStatus(ContextStatus.LOADING);
    try {
      await updateAI(wipAI!);
      setSavedAI(wipAI);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const onActionChange = (action: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        action,
      })
    );
  };

  const onBehaviourChange = (behaviour: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        behaviour,
      })
    );
  };

  const onConstraintsChange = (constraints: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        constraints,
      })
    );
  };

  const onDomainChange = (domain: string) => {
    if (wipAI === null) return;
    setWipAI(wipAI.withDomain(domain, savedAI ?? undefined));
  };

  const onFullnameChange = (fullname: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        fullname,
      })
    );
  };

  const onGenderChange = (gender: AIGender) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        gender,
      })
    );
  };

  const onIntroChange = (intro: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        intro,
      })
    );
  };

  const onLocationChange = (location: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        location,
      })
    );
  };

  const onNameChange = (name: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        name,
      })
    );
  };

  const onStatusChange = (status: AIStatus) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        status,
      })
    );
  };

  const onProfileChange = (profile: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        profile,
      })
    );
  };

  const onFirstReplyTimingChange = (firstReplyTiming: number) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        replyTiming: [firstReplyTiming, wipAI.replyTiming[1]],
      })
    );
  };

  const onSecondReplyTimingChange = (secondReplyTiming: number) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        replyTiming: [wipAI.replyTiming[0], secondReplyTiming],
      })
    );
  };

  const onFirstReminderTimingChange = (firstReminder: number) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        reminderTiming: [firstReminder, wipAI.reminderTiming[1]],
      })
    );
  };

  const onSecondReminderTimingChange = (secondReminder: number) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        reminderTiming: [wipAI.reminderTiming[0], secondReminder],
      })
    );
  };

  const onFlowTimingChange = (flowTiming: number) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        flowTiming,
      })
    );
  };

  const onTargetChange = (target: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        target,
      })
    );
  };

  const onTonChange = (ton: AITon) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        ton
      })
    );
  };

  const onWebsiteChange = (website: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        website,
      })
    );
  };

  const onFirstMessageChange = (firstMessage: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, first_message: firstMessage },
      })
    );
  };

  const onQualificationCriteriaChange = (qualificationCriteria: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, qualification_criteria: qualificationCriteria },
      })
    );
  };

  const onVoiceIdChange = (voiceId?: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, voice_id: voiceId ?? '' },
      })
    );
  };

  const onLanguageChange = (language?: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, language: language ?? '' },
      })
    );
  };

  const onPersonalityChange = (personality: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, personality },
      })
    );
  };

  const onEnvironmentChange = (environment: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, environment },
      })
    );
  };

  const onToneChange = (tone: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, tone },
      })
    );
  };

  const onGoalChange = (goal: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, goal },
      })
    );
  };

  const onClosingChange = (closing: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, closing },
      })
    );
  };

  const onGuardrailsChange = (guardrails: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, guardrails },
      })
    );
  };

  const onOtherChange = (other: string) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, other },
      })
    );
  };

  const onActivitiesChange = (activities: AIReceptionist['activities']) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, activities },
      })
    );
  };

  const onMotifsChange = (motifs: AIReceptionist['motifs']) => {
    if (wipAI === null) return;
    setWipAI(
      wipAI.copyWith({
        aiReceptionist: { ...wipAI.aiReceptionist, motifs },
      })
    );
  };

  return (
    <AIEditionFormContext.Provider
      value={{
        wipAI,
        status,
        initWithAI,
        submitUpdate,
        onActionChange,
        onBehaviourChange,
        onConstraintsChange,
        onDomainChange,
        onFullnameChange,
        onGenderChange,
        onIntroChange,
        onLocationChange,
        onNameChange,
        onStatusChange,
        onProfileChange,
        onFirstReplyTimingChange,
        onSecondReplyTimingChange,
        onFirstReminderTimingChange,
        onSecondReminderTimingChange,
        onFlowTimingChange,
        onTargetChange,
        onTonChange,
        onWebsiteChange,
        onFirstMessageChange,
        onQualificationCriteriaChange,
        onVoiceIdChange,
        onLanguageChange,
        onPersonalityChange,
        onEnvironmentChange,
        onToneChange,
        onGoalChange,
        onClosingChange,
        onGuardrailsChange,
        onOtherChange,
        onActivitiesChange,
        onMotifsChange,
      }}
    >
      {children}
    </AIEditionFormContext.Provider>
  );
};

export default AIEditionFormContext;
